import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { Crosshair, Loader2, Plus } from 'lucide-react';
import { Button } from '@components/Button';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { PermissionGuard } from '@components/PermissionGuard';
import firearmService from '@services/firearmService';
import {
  CAC_DOC_TYPE_LABELS,
  FIREARM_KIND_LABELS,
  FIREARM_STATUSES,
  FIREARM_STATUS_LABELS,
  type CacDocument,
  type Firearm,
  type FirearmStatus,
} from '@/types/cac';
import { BADGE_CLASS, CAC_STATUS_TONES, describeDaysToExpire } from '@/utils/expiry';
import { FirearmFormModal } from './FirearmFormModal';
import { FirearmDrawer } from './FirearmDrawer';

const STATUS_BADGE: Record<FirearmStatus, string> = { active: 'badge-success', sold: 'badge-neutral', transferred: 'badge-info' };

/** Badges dos documentos CAC vigentes da arma (CRAF/GT) com a cor do status. */
const CacDocBadges: React.FC<{ docs?: CacDocument[] }> = ({ docs }) => {
  if (!docs || docs.length === 0) return <span className="text-small text-slate-400">sem documentos</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {docs.map((d) => (
        <span key={d.id} className={BADGE_CLASS[CAC_STATUS_TONES[d.status]]} title={describeDaysToExpire(d.days_to_expire)}>
          {CAC_DOC_TYPE_LABELS[d.doc_type]}
          {d.status === 'expired' ? ' vencido' : d.status === 'expiring' ? ' vencendo' : ''}
        </span>
      ))}
    </div>
  );
};

/** Acervo CAC → Armas: lista (cartões no celular, tabela no desktop), ficha em drawer, cadastro em modal. */
export const FirearmListView: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState<FirearmStatus | ''>('');
  const [items, setItems] = useState<Firearm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [creating, setCreating] = useState(false);
  const openId = Number(searchParams.get('open')) > 0 ? Number(searchParams.get('open')) : null;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await firearmService.getAll(status));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const open = (id: number | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('open', String(id));
    else next.delete('open');
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-h3 flex items-center gap-2">
          <Crosshair size={20} className="text-slate-500" /> Armas
        </h1>
        <div className="grid grid-cols-1 sm:flex items-center gap-2">
          <select className="select-field" value={status} onChange={(e) => setStatus(e.target.value as FirearmStatus | '')}>
            <option value="">Todas as situações</option>
            {FIREARM_STATUSES.map((s) => (
              <option key={s} value={s}>
                {FIREARM_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
          <PermissionGuard permission="acervo.write">
            <Button variant="primary" size="sm" icon={<Plus />} onClick={() => setCreating(true)}>
              Nova arma
            </Button>
          </PermissionGuard>
        </div>
      </div>

      {/* Celular: cartões */}
      <div className="md:hidden space-y-2">
        {loading && items.length === 0 && (
          <p className="text-center text-slate-400 py-6">
            <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
          </p>
        )}
        {!loading && items.length === 0 && <p className="text-center text-slate-500 py-8">Nenhuma arma cadastrada.</p>}
        {items.map((f) => (
          <button key={f.id} type="button" onClick={() => open(f.id)} className={clsx('card p-3 w-full text-left space-y-2', loading && 'opacity-60')}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-medium text-slate-800">
                  {f.brand} {f.model}
                </p>
                <p className="text-small">
                  {FIREARM_KIND_LABELS[f.kind]} · {f.caliber}
                </p>
              </div>
              <span className={STATUS_BADGE[f.status]}>{FIREARM_STATUS_LABELS[f.status]}</span>
            </div>
            <p className="text-small [overflow-wrap:anywhere]">
              Série {f.serial_number}
              {f.sigma_number && ` · SIGMA ${f.sigma_number}`}
            </p>
            <CacDocBadges docs={f.cac_documents} />
          </button>
        ))}
      </div>

      {/* Tabela */}
      <div className="table-container bg-white hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="table-header">
              <tr>
                <th>Arma</th>
                <th>Calibre</th>
                <th>Nº de série</th>
                <th>SIGMA</th>
                <th>Situação</th>
                <th>Documentos</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="table-cell text-center text-slate-400">
                    <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="table-cell text-center py-12 text-slate-500">
                    Nenhuma arma cadastrada.
                  </td>
                </tr>
              )}
              {items.map((f) => (
                <tr key={f.id} className={clsx('table-row cursor-pointer', loading && 'opacity-60')} onClick={() => open(f.id)}>
                  <td className="table-cell">
                    <p className="font-medium text-slate-800">
                      {f.brand} {f.model}
                    </p>
                    <p className="text-small">{FIREARM_KIND_LABELS[f.kind]}</p>
                  </td>
                  <td className="table-cell whitespace-nowrap">{f.caliber}</td>
                  <td className="table-cell">{f.serial_number}</td>
                  <td className="table-cell">{f.sigma_number || <span className="text-slate-400">—</span>}</td>
                  <td className="table-cell">
                    <span className={STATUS_BADGE[f.status]}>{FIREARM_STATUS_LABELS[f.status]}</span>
                  </td>
                  <td className="table-cell">
                    <CacDocBadges docs={f.cac_documents} />
                    {f.documents_count > 0 && <p className="text-small mt-1">{f.documents_count} arquivo(s)</p>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {creating && (
        <FirearmFormModal
          onClose={() => setCreating(false)}
          onSaved={(f) => {
            setCreating(false);
            load();
            open(f.id);
          }}
          onError={setError}
        />
      )}

      {openId && <FirearmDrawer firearmId={openId} onClose={() => open(null)} onChanged={load} onError={setError} />}

      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};
